// Clients, contacts by responsibility, contracts and addresses (brief 1B-1, PLAN.md 8.3, mockup
// Clients screens and "Who gets what"). Pure: no database, no clock, no outside service.

import { isRealDay } from "./dates";
import { readTerms } from "./money";

/** The modules that have a Clients screen. */
export const CLIENT_MODULES = ["staffing", "homecare", "transport"] as const;
export type ClientModule = (typeof CLIENT_MODULES)[number];
export const isClientModule = (m: unknown): m is ClientModule =>
  (CLIENT_MODULES as readonly unknown[]).includes(m);

export const MODULE_LABEL: Record<ClientModule, string> = {
  staffing: "Staffing",
  homecare: "Home Care",
  transport: "Transportation",
};

export const KINDS = ["client", "payer", "facility", "vendor", "referral_source"] as const;
export type OrgKind = (typeof KINDS)[number];
export const KIND_LABEL: Record<OrgKind, string> = {
  client: "Client",
  payer: "Payer",
  facility: "Facility",
  vendor: "Vendor",
  referral_source: "Referral source",
};

/**
 * Client types per module, as each module's Clients screen lists them in the mockup. Only the
 * seed now (brief 1D-6): Staffing's and Transportation's are each company's own rows
 * (organization_client_types, migration 0028 seed_cleanup), Home Care's its client_types. Nothing
 * reads this list to check or offer a type.
 */
export const CLIENT_TYPES: Record<ClientModule, readonly string[]> = {
  staffing: [
    "Acute Hospital",
    "Nursing Home / SNF",
    "Assisted Living",
    "Doctor Office",
    "Home Care (internal)",
    "NEMT (internal)",
  ],
  homecare: [
    "Private pay",
    "Hospital",
    "Workers' comp",
    "Physician practice",
    "State program",
    "Senior living",
  ],
  transport: [
    "Workers' comp",
    "Private pay",
    "Facility contract",
    "Broker · purchase orders",
    "Transportation MSP",
  ],
};

/** One of the company's client types: a Staffing or Transportation row, or a Home Care type. */
export interface ClientTypeChoice {
  module: ClientModule;
  name: string;
}

/** The company's client types for a module, in their order, by name once each. */
export const typeNamesFor = (types: readonly ClientTypeChoice[], module: ClientModule) => [
  ...new Set(types.filter((t) => t.module === module).map((t) => t.name)),
];

/**
 * A client's type, checked against the company's own types (brief 1D-6): the type as the company
 * spells it, the modules' own first, or a refusal in plain words that lists the modules' types.
 * A type from another of the company's lists is kept (a client one module shares with another).
 * None is fine.
 */
export function checkClientType(
  name: string | null,
  modules: readonly ClientModule[],
  types: readonly ClientTypeChoice[],
): { ok: true; value: string | null } | { ok: false; error: string } {
  if (name === null) return { ok: true, value: null };
  const mine = types.filter((t) => modules.includes(t.module));
  const is = (t: ClientTypeChoice) => t.name.trim().toLowerCase() === name.trim().toLowerCase();
  const found = mine.find(is) ?? types.find(is);
  if (found) return { ok: true, value: found.name };
  const where = modules.map((m) => MODULE_LABEL[m]).join(" or ");
  const names = [...new Set(mine.map((t) => t.name))];
  return {
    ok: false,
    error: names.length
      ? `"${name}" isn't one of the company's ${where} client types. Pick one of: ${names.join(", ")}.`
      : `The company has no ${where} client types yet, so leave the type empty for now.`,
  };
}

/**
 * Client onboarding stages kept in code: only Transportation's, which has no workflow in the
 * mockup yet. Staffing and Home Care onboarding are workflows, as data (brief 1B-7,
 * server/rules/workflows.ts CLIENT_WORKFLOW).
 */
export const CODE_STAGES = {
  transport: ["New", "Contract", "Trip rules", "Billing setup", "Active"],
} as const satisfies Partial<Record<ClientModule, readonly string[]>>;
export type CodeStageModule = keyof typeof CODE_STAGES;
export const hasCodeStages = (m: ClientModule): m is CodeStageModule => m in CODE_STAGES;

/** The line under each of those stages on the record (mockup stageCard hints). */
export const CODE_STAGE_HINTS: Record<CodeStageModule, readonly string[]> = {
  transport: [
    "Enter the client.",
    "Contract and dates.",
    "Trip rules: authorization, wait time, cancellations.",
    "Billing format and rates.",
    "Active.",
  ],
};

/** A client's stage in a module whose stages are in code; a module it's new to starts at New. */
export function stageIn(
  stages: Readonly<Record<string, unknown>>,
  module: CodeStageModule,
): number {
  const n = Number(stages[module] ?? 0);
  const last = CODE_STAGES[module].length - 1;
  return Number.isInteger(n) ? Math.min(Math.max(n, 0), last) : 0;
}

/** The stage a client moves to: on to the next one, or back one. Null when it can't move. */
export function moveStage(
  stages: Readonly<Record<string, unknown>>,
  module: CodeStageModule,
  way: "next" | "back",
): Record<string, number> | null {
  const now = stageIn(stages, module);
  const to = way === "next" ? now + 1 : now - 1;
  if (to < 0 || to >= CODE_STAGES[module].length) return null;
  return { ...(stages as Record<string, number>), [module]: to };
}

export const CHANNELS = ["email", "portal", "text", "fax", "mail"] as const;
export type Channel = (typeof CHANNELS)[number];
export const CHANNEL_LABEL: Record<Channel, string> = {
  email: "Email",
  portal: "Portal",
  text: "Text",
  fax: "Fax",
  mail: "Mail",
};

// ---- Reading what a screen sends ------------------------------------------------------------

type Read<T> = { ok: true; value: T } | { ok: false; error: string };
const no = <T>(error: string): Read<T> => ({ ok: false, error });

/** Trimmed text with spaces collapsed, or null when empty; refused past `max` characters. */
function text(v: unknown, max: number): string | null | undefined {
  if (v === undefined) return undefined;
  if (v === null) return null;
  if (typeof v !== "string") return undefined;
  const t = v.replace(/\s+/g, " ").trim();
  if (!t) return null;
  return t.length > max ? undefined : t;
}

function obj(input: unknown): Record<string, unknown> | null {
  return typeof input === "object" && input !== null && !Array.isArray(input)
    ? (input as Record<string, unknown>)
    : null;
}

export interface ClientFields {
  name?: string;
  kind?: OrgKind;
  client_type?: string | null;
  modules?: ClientModule[];
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}

const ADDRESS = ["address_line1", "address_line2", "city", "state", "zip"] as const;
const ADDRESS_KEYS: Record<(typeof ADDRESS)[number], string> = {
  address_line1: "addressLine1",
  address_line2: "addressLine2",
  city: "city",
  state: "state",
  zip: "zip",
};

/**
 * Reads a new client or a change to one. A new client needs a name and at least one module.
 * Only what was sent is changed.
 */
export function readClient(input: unknown, creating: boolean): Read<ClientFields> {
  const b = obj(input);
  if (!b) return no("Send the client's details.");
  const out: ClientFields = {};
  if (b.name !== undefined || creating) {
    const name = text(b.name, 120);
    if (!name) return no("The client needs a name (up to 120 characters).");
    out.name = name;
  }
  if (b.kind !== undefined) {
    if (!(KINDS as readonly unknown[]).includes(b.kind))
      return no("Pick what kind of client this is.");
    out.kind = b.kind as OrgKind;
  }
  // The type is checked against the company's own types where it's saved (checkClientType).
  if (b.clientType !== undefined) {
    if (b.clientType === null || b.clientType === "") out.client_type = null;
    else {
      const t = text(b.clientType, 60);
      if (!t) return no("Pick a client type from the list.");
      out.client_type = t;
    }
  }
  if (b.modules !== undefined || creating) {
    const m = Array.isArray(b.modules) ? [...new Set(b.modules)] : [];
    if (m.length === 0 || !m.every(isClientModule))
      return no("Say which of Staffing, Home Care and Transportation use this client.");
    out.modules = CLIENT_MODULES.filter((x) => m.includes(x));
  }
  for (const col of ADDRESS) {
    const key = ADDRESS_KEYS[col];
    if (b[key] === undefined) continue;
    const v = text(b[key], col === "state" ? 2 : col === "zip" ? 10 : 120);
    if (v === undefined) return no(`Check the address: ${ADDRESS_WORDS[col]} is too long.`);
    if (col === "state" && v && !/^[A-Za-z]{2}$/.test(v)) return no("Use the two-letter state.");
    if (col === "zip" && v && !/^\d{5}(-\d{4})?$/.test(v)) return no("Use a 5-digit ZIP code.");
    out[col] = col === "state" && v ? v.toUpperCase() : v;
  }
  return { ok: true, value: out };
}
const ADDRESS_WORDS: Record<(typeof ADDRESS)[number], string> = {
  address_line1: "the street",
  address_line2: "the second line",
  city: "the city",
  state: "the state",
  zip: "the ZIP code",
};

export interface UnitFields {
  name?: string;
  kind?: "site" | "unit";
}
export function readUnit(input: unknown, creating: boolean): Read<UnitFields> {
  const b = obj(input);
  if (!b) return no("Send the site or unit.");
  const out: UnitFields = {};
  if (b.name !== undefined || creating) {
    const name = text(b.name, 120);
    if (!name) return no("The site or unit needs a name.");
    out.name = name;
  }
  if (b.kind !== undefined || creating) {
    const kind = b.kind ?? "site";
    if (kind !== "site" && kind !== "unit") return no("Say whether it's a site or a unit.");
    out.kind = kind;
  }
  return { ok: true, value: out };
}

export interface ContactFields {
  name?: string;
  title?: string | null;
  email?: string | null;
  phone?: string | null;
  fax?: string | null;
  notes?: string | null;
}
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^[0-9()+.\-\s]{7,20}(\s*(x|ext\.?)\s*\d{1,6})?$/i;

export function readContact(input: unknown, creating: boolean): Read<ContactFields> {
  const b = obj(input);
  if (!b) return no("Send the contact's details.");
  const out: ContactFields = {};
  if (b.name !== undefined || creating) {
    const name = text(b.name, 120);
    if (!name) return no("The contact needs a name.");
    out.name = name;
  }
  const fields = [
    ["title", 80],
    ["email", 160],
    ["phone", 30],
    ["fax", 30],
    ["notes", 1000],
  ] as const;
  for (const [k, max] of fields) {
    if (b[k] === undefined) continue;
    const v = text(b[k], max);
    if (v === undefined) return no(`The ${k} is too long.`);
    if (k === "email" && v && !EMAIL.test(v)) return no("Check the email address.");
    if ((k === "phone" || k === "fax") && v && !PHONE.test(v)) return no(`Check the ${k} number.`);
    out[k] = k === "email" && v ? v.toLowerCase() : v;
  }
  return { ok: true, value: out };
}

export interface ContractFields {
  business_id?: string | null;
  number?: string | null;
  starts_on?: string | null;
  ends_on?: string | null;
  terms?: string | null;
  signed_file_id?: string | null;
  /** Payment terms in net days (brief 1E-1); kept in the contract's settings. */
  terms_days?: number;
}
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
// One date rule for every date field (brief 1E-23).
const isDay = (s: string) => isRealDay(s);

/** Reads a contract. Dates are YYYY-MM-DD; an empty end date means it doesn't end (evergreen). */
export function readContract(
  input: unknown,
  current: { starts_on: string | null; ends_on: string | null } = {
    starts_on: null,
    ends_on: null,
  },
): Read<ContractFields> {
  const b = obj(input);
  if (!b) return no("Send the contract's details.");
  const out: ContractFields = {};
  for (const [k, key] of [
    ["business_id", "businessId"],
    ["signed_file_id", "signedFileId"],
  ] as const) {
    if (b[key] === undefined) continue;
    if (b[key] === null || b[key] === "") out[k] = null;
    else if (typeof b[key] === "string" && UUID.test(b[key])) out[k] = b[key];
    else
      return no(
        k === "business_id" ? "Pick one of the company's businesses." : "Pick the signed file.",
      );
  }
  if (b.number !== undefined) {
    const v = text(b.number, 60);
    if (v === undefined) return no("The contract number is too long.");
    out.number = v;
  }
  if (b.terms !== undefined) {
    const v = text(b.terms, 2000);
    if (v === undefined) return no("The terms are too long.");
    out.terms = v;
  }
  if (b.termsDays !== undefined && b.termsDays !== null && b.termsDays !== "") {
    const read = readTerms(b.termsDays);
    if (!read.ok) return read;
    out.terms_days = read.value;
  }
  for (const [k, key, words] of [
    ["starts_on", "startsOn", "start"],
    ["ends_on", "endsOn", "end"],
  ] as const) {
    if (b[key] === undefined) continue;
    if (b[key] === null || b[key] === "") out[k] = null;
    else if (typeof b[key] === "string" && isDay(b[key])) out[k] = b[key];
    else return no(`Check the ${words} date.`);
  }
  const starts = out.starts_on !== undefined ? out.starts_on : current.starts_on;
  const ends = out.ends_on !== undefined ? out.ends_on : current.ends_on;
  if (starts && ends && ends < starts) return no("The contract can't end before it starts.");
  return { ok: true, value: out };
}

export interface ResponsibilityFields {
  module: ClientModule;
  name: string;
  gets: string;
}
export function readResponsibility(input: unknown): Read<ResponsibilityFields> {
  const b = obj(input);
  if (!b) return no("Send the responsibility.");
  if (!isClientModule(b.module)) return no("Say which module it's for.");
  const name = text(b.name, 60);
  if (!name) return no("Name the responsibility (up to 60 characters).");
  const gets = text(b.gets ?? "", 120);
  if (gets === undefined) return no("Keep what they get to 120 characters.");
  return { ok: true, value: { module: b.module, name, gets: gets ?? "" } };
}

/** Whether a reason was given for archiving. Returns why it's refused, or null. */
export function refuseArchiveReason(reason: unknown): string | null {
  return typeof reason === "string" && reason.trim() ? null : "Say why it's archived.";
}

// ---- Addresses and map points ---------------------------------------------------------------

export interface Address {
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

/** The address as one line, for looking it up and showing it; null when there's no street. */
export function addressLine(a: Address): string | null {
  if (!a.address_line1) return null;
  const cityState = [a.city, [a.state, a.zip].filter(Boolean).join(" ")].filter(Boolean);
  return [a.address_line1, a.address_line2, ...cityState].filter(Boolean).join(", ");
}

export type MapStatus = "none" | "pending" | "found" | "not_found";

/**
 * Whether saving these fields means looking the address up: only when the address is new or
 * changed from the one the map point was found for. Reads never look it up.
 */
export function needsLookup(after: Address, mapAddress: string | null): boolean {
  const line = addressLine(after);
  return line !== null && line !== mapAddress;
}

/** What the record says about its map point. */
export function mapState(status: MapStatus): { label: string; level: "ok" | "warn" | "plain" } {
  switch (status) {
    case "found":
      return { label: "On the map", level: "ok" };
    case "not_found":
      return { label: "Couldn't find this address, check it", level: "warn" };
    case "pending":
      return { label: "Not on the map yet", level: "plain" };
    case "none":
      return { label: "No address", level: "plain" };
  }
}

// ---- Contract dates -------------------------------------------------------------------------

/** Days from one New York date to another (YYYY-MM-DD). */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / 86_400_000);
}

/** MM/DD/YYYY, as the screens show dates. */
export const usDate = (d: string) => `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(0, 4)}`;

/** A contract ending in this many days or fewer is flagged. */
export const ENDING_SOON_DAYS = 60;

/**
 * The warning chip for a contract's end date, or null: ended contracts say so, and one ending in
 * 60 days or less says how many days are left. No end date never warns (evergreen).
 */
export function contractWarning(
  endsOn: string | null,
  today: string,
): { level: "warn" | "bad"; text: string } | null {
  if (!endsOn) return null;
  const days = daysBetween(today, endsOn);
  if (days < 0) return { level: "bad", text: `Ended ${usDate(endsOn)}` };
  if (days > ENDING_SOON_DAYS) return null;
  if (days === 0) return { level: "warn", text: "Ends today" };
  return { level: "warn", text: `Ends in ${days} day${days === 1 ? "" : "s"}` };
}

/**
 * The contract a client's list row shows: the one that ends last among those not archived (no
 * end date counts as last).
 */
export function currentContract<C extends { ends_on: string | null; archived: boolean }>(
  contracts: readonly C[],
): C | null {
  const live = contracts.filter((c) => !c.archived);
  if (!live.length) return null;
  return live.reduce((best, c) =>
    best.ends_on === null ? best : c.ends_on === null || c.ends_on > best.ends_on ? c : best,
  );
}

// ---- Who gets what --------------------------------------------------------------------------

/** The ways a person can get a responsibility (brief 1E-15, mockup v65): one or more. */
export const WAYS = ["email", "fax", "portal_account", "portal_link", "text", "mail"] as const;
export type Way = (typeof WAYS)[number];
export const WAY_LABEL: Record<Way, string> = {
  email: "Email",
  fax: "Fax",
  portal_account: "Portal account",
  portal_link: "Portal link that expires",
  text: "Text",
  mail: "Mail",
};
export const isWay = (w: unknown): w is Way => (WAYS as readonly unknown[]).includes(w);

/** The one way a row had before brief 1E-15, as a way now (portal becomes portal account). */
export const wayOf = (channel: Channel): Way => (channel === "portal" ? "portal_account" : channel);

/**
 * The ways picked on the sheet: each one known, once each, in the sheet's order; at least one.
 * A single `channel` (the old screens) reads as that one way.
 */
export function readWays(input: unknown): Read<Way[]> {
  const list = Array.isArray(input)
    ? input
    : typeof input === "string"
      ? [
          isWay(input)
            ? input
            : (CHANNELS as readonly string[]).includes(input)
              ? wayOf(input as Channel)
              : input,
        ]
      : null;
  if (!list) return no("Pick at least one way.");
  if (list.some((w) => !isWay(w))) return no("Pick how they get it.");
  const ways = WAYS.filter((w) => list.includes(w));
  if (!ways.length) return no("Pick at least one way.");
  return { ok: true, value: ways };
}

export interface WhoContact {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  fax: string | null;
  archived: boolean;
}
export interface WhoAssignment {
  responsibility_id: string;
  contact_id: string;
  channels: readonly Way[];
}

export type ContactFor =
  | { ok: true; contact: WhoContact; ways: { way: Way; to: string | null }[]; message: string }
  | { ok: false; contact: WhoContact | null; message: string };

/** Where a way sends to for a contact: the email, the number for texts or faxes. */
function sendsTo(contact: WhoContact, way: Way): string | null {
  if (way === "email") return contact.email;
  if (way === "text") return contact.phone;
  if (way === "fax") return contact.fax;
  return null;
}

/** What a way needs on the contact before anything can go that way. */
const NEEDS: Partial<Record<Way, string>> = {
  email: "an email address",
  fax: "a fax number",
  text: "a phone number",
};

/** "email and fax", "email, fax and mail". */
export function waysWords(ways: readonly Way[]): string {
  const w = ways.map((x) => WAY_LABEL[x].toLowerCase());
  return w.length < 2 ? (w[0] ?? "") : `${w.slice(0, -1).join(", ")} and ${w[w.length - 1]}`;
}

/**
 * Who at a client gets one responsibility, and every way they get it. Sending follows every way
 * picked. When nobody is set, or the person has left (archived), or a way is missing its email or
 * number ("Add a fax number for {person}."), it says so in plain words.
 */
export function contactFor(
  organization: { name: string },
  responsibility: { id: string; name: string },
  who: { assignments: readonly WhoAssignment[]; contacts: readonly WhoContact[] },
): ContactFor {
  const set = who.assignments.find((a) => a.responsibility_id === responsibility.id);
  const contact = set && who.contacts.find((c) => c.id === set.contact_id);
  if (!set || !contact)
    return {
      ok: false,
      contact: null,
      message: `Nobody at ${organization.name} is set for ${responsibility.name}. Set it under Who gets what.`,
    };
  if (contact.archived)
    return {
      ok: false,
      contact,
      message: `${contact.name} no longer handles ${responsibility.name} at ${organization.name}. Pick someone else under Who gets what.`,
    };
  const ways = WAYS.filter((w) => set.channels.includes(w)).map((way) => ({
    way,
    to: sendsTo(contact, way),
  }));
  const missing = ways.filter((w) => NEEDS[w.way] && !w.to);
  if (missing.length)
    return {
      ok: false,
      contact,
      message: missing.map((w) => `Add ${NEEDS[w.way]} for ${contact.name}.`).join(" "),
    };
  return {
    ok: true,
    contact,
    ways,
    message: `${contact.name} gets ${responsibility.name} by ${ways
      .map((w) => `${WAY_LABEL[w.way].toLowerCase()}${w.to ? ` (${w.to})` : ""}`)
      .join(ways.length > 2 ? ", " : " and ")
      .replace(/, ([^,]*)$/, " and $1")}.`,
  };
}

/** The responsibilities invoices and payments go to: Billing, Payments, Invoices and payments. */
export const isMoneyResponsibility = (name: string) => /^(Billing|Payments|Invoices)\b/.test(name);

/**
 * The card's warning (mockup v65): the money responsibilities with nobody set (or a person who has
 * left), as "Billing and Payments: nobody set.", or null when each has someone.
 */
export function nobodySetWords(
  rows: readonly { name: string; contactId: string | null; contactArchived: boolean }[],
): string | null {
  const gaps = rows
    .filter((r) => isMoneyResponsibility(r.name) && (!r.contactId || r.contactArchived))
    .map((r) => r.name);
  if (!gaps.length) return null;
  return `${gaps.length < 2 ? gaps[0] : `${gaps.slice(0, -1).join(", ")} and ${gaps[gaps.length - 1]}`}: nobody set.`;
}

/**
 * Bill the week's flag for a client nobody receives invoices for (its Billing has nobody, or a
 * person who has left). A flag only: the invoice can still be made.
 */
export function nobodyReceivesInvoices(
  client: string,
  billing: { contactId: string | null; contactArchived: boolean } | null,
): string | null {
  return billing?.contactId && !billing.contactArchived
    ? null
    : `Nobody receives invoices for ${client}. Set Billing in Who gets what.`;
}

// ---- How a client is billed (brief 1E-15) -----------------------------------------------------------

/** Invoice, or Invoice and CMS-1500: set on each Home Care client type, taken by a new client. */
export const BILLS_BY = ["invoice", "invoice_cms1500"] as const;
export type BillsBy = (typeof BILLS_BY)[number];
export const BILLS_BY_LABEL: Record<BillsBy, string> = {
  invoice: "Invoice",
  invoice_cms1500: "Invoice and CMS-1500",
};
export const isBillsBy = (v: unknown): v is BillsBy => (BILLS_BY as readonly unknown[]).includes(v);

/** Reads a Bills by choice from a screen. */
export function readBillsBy(v: unknown): Read<BillsBy> {
  return isBillsBy(v) ? { ok: true, value: v } : no("Pick how the client is billed.");
}

/** What a new client bills by: its type's choice, else Invoice. */
export const newClientBillsBy = (type: { bills_by: BillsBy } | null | undefined): BillsBy =>
  type?.bills_by ?? "invoice";

/** Whether a client's screens show claim words (CMS-1500, claim, corrected claim): only with claims. */
export const showsClaimWords = (billsBy: BillsBy) => billsBy === "invoice_cms1500";
